import { createContext, useCallback, useContext, useId, useLayoutEffect, useRef, useState } from 'react';
import './Forms.css';

const FormContext = createContext({ valid: true, issues: [] });
export const useFormReadiness = () => useContext(FormContext);

function fieldLabel(field) {
  const label = field.labels?.[0];
  return (field.getAttribute('aria-label') || label?.querySelector('.form-label-text')?.textContent || label?.textContent || field.name || 'Field').trim();
}

function collectIssues(form) {
  return [...form.elements].filter(field => {
    if (!field.validity || field.matches(':disabled') || field.readOnly || field.type === 'hidden') return false;
    if (field.type === 'file') return field.required && !field.files?.length;
    if (field.type === 'email' && field.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim())) return true;
    return !field.validity.valid || (field.required && typeof field.value === 'string' && !field.value.trim());
  }).map(field => ({ field, label: fieldLabel(field) }));
}

export function focusFormField(field) {
  field.dispatchEvent(new CustomEvent('reveal-form-field', { bubbles: true }));
  requestAnimationFrame(() => {
    field.focus();
    field.scrollIntoView?.({ block: 'nearest', behavior: 'instant' });
  });
}

export function FormReadiness() {
  const { issues, ready, readinessMessage, hintId, showFeedback } = useFormReadiness();
  if (!showFeedback || (!issues.length && ready !== false)) return null;
  return <div className="form-readiness" id={hintId}>
    {issues.length > 0 && <><span>Complete or correct: </span>{issues.map(({ field, label }, index) => <span key={index}>
      {index > 0 && ', '}<button type="button" className="form-issue-link" onClick={() => focusFormField(field)}>{label}</button>
    </span>)}.</>}
    {ready === false && readinessMessage && <span className="form-readiness-extra">{readinessMessage}</span>}
  </div>;
}

export function SubmitButton({ disabled, ...props }) {
  const { valid, hintId, showFeedback } = useFormReadiness();
  return <button {...props} type="submit" disabled={disabled || !valid} aria-describedby={!valid && showFeedback ? hintId : props['aria-describedby']} />;
}

export default function ValidatedForm({ children, ready = true, readinessMessage = '', showFeedbackOnSubmit = false, onSubmit, onChangeCapture, onInputCapture, onReset, ...props }) {
  const form = useRef(null);
  const hintId = useId();
  const [issues, setIssues] = useState([]);
  const [invalidSubmit, setInvalidSubmit] = useState(false);
  const issuesRef = useRef([]);
  const refresh = useCallback(() => {
    if (!form.current) return;
    const next = collectIssues(form.current);
    const current = issuesRef.current;
    if (current.length === next.length && current.every((item, index) => item.field === next[index].field && item.label === next[index].label)) return;
    issuesRef.current = next;
    setIssues(next);
  }, []);
  useLayoutEffect(refresh);
  return <FormContext.Provider value={{ valid: ready && !issues.length, ready, issues, readinessMessage, hintId, showFeedback: !showFeedbackOnSubmit || invalidSubmit }}>
    <form {...props} ref={form} noValidate
      onInputCapture={event => { onInputCapture?.(event); refresh(); }}
      onChangeCapture={event => { onChangeCapture?.(event); refresh(); }}
      onReset={event => { onReset?.(event); setInvalidSubmit(false); queueMicrotask(refresh); }}
      onSubmit={event => {
        const current = collectIssues(event.currentTarget);
        if (!ready || current.length || event.nativeEvent.submitter?.disabled) {
          event.preventDefault();
          setInvalidSubmit(true);
          refresh();
          if (current[0]) focusFormField(current[0].field);
          return;
        }
        setInvalidSubmit(false);
        onSubmit?.(event);
      }}
    >{children}</form>
  </FormContext.Provider>;
}
